// 数据处理工具函数

/**
 * 从虾塘数据中提取省份信息
 * @param {Object} pond - 虾塘数据对象
 * @returns {string} - 省份名称（不带后缀）
 */
export const extractProvinceFromPond = (pond) => {
  // 从addressBookVO.provinceName字段提取省份信息
  if (pond.addressBookVO && pond.addressBookVO.provinceName) {
    const provinceName = pond.addressBookVO.provinceName;

    // 省份名称正则表达式，匹配带后缀的省份名称，如"广东省"、"北京市"等
    const provinceRegex =
      /(北京|天津|河北|山西|内蒙古|辽宁|吉林|黑龙江|上海|江苏|浙江|安徽|福建|江西|山东|河南|湖北|湖南|广东|广西|海南|重庆|四川|贵州|云南|西藏|陕西|甘肃|青海|宁夏|新疆|香港|澳门|台湾)(?:省|市|自治区|特别行政区)?/;
    const provinceMatches = provinceName.match(provinceRegex);
    if (provinceMatches) {
      // 返回匹配到的省份名称（不带后缀）
      return provinceMatches[1];
    } else {
      // 直接处理常见情况，如"广东省" -> "广东"
      return provinceName.replace(/[省市自治区特别行政区]/g, "");
    }
  }
  return "";
};

/**
 * 从虾塘数据中提取城市信息
 * @param {Object} pond - 虾塘数据对象
 * @returns {string} - 城市名称（不带后缀）
 */
export const extractCityFromPond = (pond) => {
  // 从addressBookVO.cityName字段提取城市信息
  if (pond.addressBookVO && pond.addressBookVO.cityName) {
    let cityName = pond.addressBookVO.cityName;

    // 直接去除后缀，保留完整城市名称
    // 处理顺序：先处理特殊情况，再处理一般情况
    const citySuffixes = ["市", "地区", "自治州", "盟", "区", "县"];
    let extractedCityName = cityName;

    // 遍历所有后缀，去除匹配的后缀
    for (const suffix of citySuffixes) {
      if (extractedCityName.endsWith(suffix)) {
        extractedCityName = extractedCityName.slice(0, -suffix.length);
        break; // 只去除第一个匹配的后缀
      }
    }

    // 特殊处理：如果去除后缀后为空，返回原始名称
    if (!extractedCityName) {
      extractedCityName = cityName;
    }

    return extractedCityName;
  }
  return "";
};

/**
 * 从虾塘数据中提取区县信息
 * @param {Object} pond - 虾塘数据对象
 * @returns {string} - 完整的区县名称（保留后缀）
 */
export const extractDistrictFromPond = (pond) => {
  // 从addressBookVO.districtName字段提取区县信息
  if (pond.addressBookVO && pond.addressBookVO.districtName) {
    // 保留完整的区县名称，包括后缀（如"增城区"、"白云区"）
    // 这是为了与地图上显示的区县名称保持一致
    return pond.addressBookVO.districtName;
  }
  return "";
};

/**
 * 处理区县数据
 * @param {Array} ponds - 虾塘数据数组
 * @param {Array} devices - 设备数据数组
 * @param {string} provinceName - 目标省份名称
 * @param {string} cityName - 目标城市名称
 * @returns {Array} - 处理后的区县数据
 */
export const processDistrictData = (ponds, devices, provinceName, cityName) => {
  // 初始化区县数据Map
  const districtDataMap = new Map();

  // 筛选出目标省份和城市的虾塘
  const cityPonds = ponds.filter((pond) => {
    const pondProvince = extractProvinceFromPond(pond);
    const pondCity = extractCityFromPond(pond);
    return pondProvince === provinceName && pondCity === cityName;
  });

  // 统计区县虾塘数量
  if (cityPonds.length > 0) {
    cityPonds.forEach((pond) => {
      const districtName = extractDistrictFromPond(pond);
      if (districtName) {
        if (!districtDataMap.has(districtName)) {
          districtDataMap.set(districtName, {
            name: districtName,
            value: 0,
            pondCount: 0,
            deviceCount: 0,
            onlineDevices: 0,
            offlineDevices: 0,
          });
        }
        const districtData = districtDataMap.get(districtName);
        districtData.pondCount += 1;
        districtData.value += 1;
      }
    });
  }

  // 统计区县设备数量
  if (devices.length > 0) {
    devices.forEach((device) => {
      let assignedDistrict = null;
      let assignedProvince = null;
      let assignedCity = null;

      // 方法1：如果设备有pondId字段，从虾塘数据中查找对应虾塘
      if (device.pondId) {
        const relatedPond = ponds.find((pond) => pond.id === device.pondId);
        if (relatedPond) {
          assignedProvince = extractProvinceFromPond(relatedPond);
          assignedCity = extractCityFromPond(relatedPond);
          assignedDistrict = extractDistrictFromPond(relatedPond);
        }
      }

      // 方法2：如果设备有pond对象，直接从pond中提取区县、城市和省份信息
      else if (device.pond) {
        assignedProvince = extractProvinceFromPond(device.pond);
        assignedCity = extractCityFromPond(device.pond);
        assignedDistrict = extractDistrictFromPond(device.pond);
      }

      // 方法3：如果设备有坐标，尝试通过坐标匹配虾塘
      else if (device.coordinate) {
        try {
          // 解析坐标
          const coordStr =
            typeof device.coordinate === "string"
              ? device.coordinate
              : JSON.stringify(device.coordinate);
          const coord = JSON.parse(coordStr);
          if (coord.lat && coord.lon) {
            // 查找距离该坐标最近的虾塘
            let closestPond = null;
            let minDistance = Infinity;

            ponds.forEach((pond) => {
              if (pond.coordinate) {
                try {
                  const pondCoordStr =
                    typeof pond.coordinate === "string"
                      ? pond.coordinate
                      : JSON.stringify(pond.coordinate);
                  const pondCoord = JSON.parse(pondCoordStr);
                  if (pondCoord.lat && pondCoord.lon) {
                    // 计算距离（简化计算，不考虑地球曲率）
                    const distance = Math.sqrt(
                      Math.pow(coord.lat - pondCoord.lat, 2) +
                        Math.pow(coord.lon - pondCoord.lon, 2)
                    );
                    if (distance < minDistance) {
                      minDistance = distance;
                      closestPond = pond;
                    }
                  }
                } catch (e) {
                  // 解析失败，跳过
                }
              }
            });

            // 如果找到最近的虾塘，使用该虾塘的区县、城市和省份
            if (closestPond) {
              assignedProvince = extractProvinceFromPond(closestPond);
              assignedCity = extractCityFromPond(closestPond);
              assignedDistrict = extractDistrictFromPond(closestPond);
            }
          }
        } catch (e) {
          // 解析坐标失败，跳过
        }
      }

      // 只有确定了区县、城市和省份，且与目标省份和城市匹配的设备才统计
      if (
        assignedProvince === provinceName &&
        assignedCity === cityName &&
        assignedDistrict
      ) {
        // 如果区县数据Map中还没有该区县，初始化它
        if (!districtDataMap.has(assignedDistrict)) {
          districtDataMap.set(assignedDistrict, {
            name: assignedDistrict,
            value: 0,
            pondCount: 0,
            deviceCount: 0,
            onlineDevices: 0,
            offlineDevices: 0,
          });
        }

        // 更新区县设备数据
        const districtData = districtDataMap.get(assignedDistrict);
        districtData.deviceCount += 1;
        if (device.status === "online") {
          districtData.onlineDevices += 1;
        } else {
          districtData.offlineDevices += 1;
        }
      }
    });
  }

  // 转换为数组
  return Array.from(districtDataMap.values());
};

/**
 * 处理省份数据
 * @param {Array} ponds - 虾塘数据数组
 * @param {Array} devices - 设备数据数组
 * @returns {Array} - 处理后的省份数据
 */
export const processProvinceData = (ponds, devices) => {
  // 所有省份列表
  const allProvinces = [
    "北京",
    "天津",
    "河北",
    "山西",
    "内蒙古",
    "辽宁",
    "吉林",
    "黑龙江",
    "上海",
    "江苏",
    "浙江",
    "安徽",
    "福建",
    "江西",
    "山东",
    "河南",
    "湖北",
    "湖南",
    "广东",
    "广西",
    "海南",
    "重庆",
    "四川",
    "贵州",
    "云南",
    "西藏",
    "陕西",
    "甘肃",
    "青海",
    "宁夏",
    "新疆",
    "香港",
    "澳门",
    "台湾",
  ];

  // 初始化省份数据
  const provinceDataMap = {};
  allProvinces.forEach((provinceName) => {
    provinceDataMap[provinceName] = {
      name: provinceName,
      value: 0,
      pondCount: 0,
      deviceCount: 0,
      onlineDevices: 0,
      offlineDevices: 0,
    };
  });

  // 统计省份虾塘数量
  if (ponds.length > 0) {
    ponds.forEach((pond) => {
      const provinceName = extractProvinceFromPond(pond);
      if (provinceName && provinceDataMap[provinceName]) {
        provinceDataMap[provinceName].pondCount += 1;
        provinceDataMap[provinceName].value += 1;
      }
    });
  }

  // 统计省份设备数量
  if (devices.length > 0) {
    devices.forEach((device) => {
      let assignedProvince = null;

      // 尝试从device.pond中提取省份
      if (device.pond) {
        assignedProvince = extractProvinceFromPond(device.pond);
      }
      // 如果没有pond关联，尝试从坐标判断省份
      else if (device.coordinate) {
        try {
          // 解析坐标
          const coordStr =
            typeof device.coordinate === "string"
              ? device.coordinate
              : JSON.stringify(device.coordinate);
          const coord = JSON.parse(coordStr);
          if (coord.lat && coord.lon) {
            // 简单判断：根据经纬度大致确定省份
            // 23-25°N, 112-115°E 大致是广东省
            if (
              coord.lat >= 20 &&
              coord.lat <= 26 &&
              coord.lon >= 109 &&
              coord.lon <= 117
            ) {
              assignedProvince = "广东";
            }
          }
        } catch (e) {
          // 解析坐标失败，跳过
        }
      }

      // 只有确定了省份的设备才统计，没有坐标且没有pond关联的设备不统计
      // 移除默认分配逻辑，严格按照实际关联统计
      if (assignedProvince && provinceDataMap[assignedProvince]) {
        provinceDataMap[assignedProvince].deviceCount += 1;
        if (device.status === "online") {
          provinceDataMap[assignedProvince].onlineDevices += 1;
        } else {
          provinceDataMap[assignedProvince].offlineDevices += 1;
        }
      }
    });
  }

  // 转换为数组，保留所有省份
  return Object.values(provinceDataMap);
};

/**
 * 处理城市数据
 * @param {Array} ponds - 虾塘数据数组
 * @param {Array} devices - 设备数据数组
 * @param {string} provinceName - 目标省份名称
 * @returns {Array} - 处理后的城市数据
 */
export const processCityData = (ponds, devices, provinceName) => {
  // 初始化城市数据Map
  const cityDataMap = new Map();

  // 筛选出目标省份的虾塘
  const provincePonds = ponds.filter((pond) => {
    const pondProvince = extractProvinceFromPond(pond);
    return pondProvince === provinceName;
  });

  // 统计城市虾塘数量
  if (provincePonds.length > 0) {
    provincePonds.forEach((pond) => {
      const cityName = extractCityFromPond(pond);
      if (cityName) {
        if (!cityDataMap.has(cityName)) {
          cityDataMap.set(cityName, {
            name: cityName,
            value: 0,
            pondCount: 0,
            deviceCount: 0,
            onlineDevices: 0,
            offlineDevices: 0,
          });
        }
        const cityData = cityDataMap.get(cityName);
        cityData.pondCount += 1;
        cityData.value += 1;
      }
    });
  }

  // 统计城市设备数量
  // 注意：从截图中看，设备数据可能没有直接关联到pond，这里简化处理
  // 实际项目中，应该根据设备的pondId或其他关联字段来匹配虾塘
  if (devices.length > 0) {
    devices.forEach((device) => {
      // 尝试根据设备坐标或其他信息关联到城市
      let cityName = "";

      // 如果设备有pond关联，从pond中提取城市信息
      if (device.pond) {
        const deviceProvince = extractProvinceFromPond(device.pond);
        if (deviceProvince === provinceName) {
          cityName = extractCityFromPond(device.pond);
        }
      } else {
        // 没有直接关联时，尝试根据坐标判断城市
        if (device.coordinate) {
          try {
            // 解析坐标
            const coordStr =
              typeof device.coordinate === "string"
                ? device.coordinate
                : JSON.stringify(device.coordinate);
            const coord = JSON.parse(coordStr);
            // 严格检查坐标是否有效
            if (
              coord &&
              typeof coord === "object" &&
              coord.lat &&
              coord.lon &&
              !isNaN(parseFloat(coord.lat)) &&
              !isNaN(parseFloat(coord.lon))
            ) {
              const lat = parseFloat(coord.lat);
              const lon = parseFloat(coord.lon);
              // 23-25°N, 112-115°E 大致是广东省
              if (lat >= 20 && lat <= 26 && lon >= 109 && lon <= 117) {
                // 对于广东省设备，根据坐标更精确判断城市
                // 23.0-23.5°N, 113.0-113.8°E 大致是广州市
                if (
                  lat >= 23.0 &&
                  lat <= 23.5 &&
                  lon >= 113.0 &&
                  lon <= 113.8
                ) {
                  cityName = "广州";
                }
                // 可以根据需要添加更多城市的坐标范围判断
              }
            }
          } catch (e) {
            // 解析坐标失败，跳过
          }
        }
      }

      // 只有确定了城市的设备才统计，没有坐标且没有pond关联的设备不统计
      // 移除默认分配逻辑，严格按照实际关联统计
      if (cityName) {
        // 如果城市数据Map中还没有该城市，初始化它
        if (!cityDataMap.has(cityName)) {
          cityDataMap.set(cityName, {
            name: cityName,
            value: 0,
            pondCount: 0,
            deviceCount: 0,
            onlineDevices: 0,
            offlineDevices: 0,
          });
        }

        // 更新城市设备数据
        const cityData = cityDataMap.get(cityName);
        cityData.deviceCount += 1;
        if (device.status === "online") {
          cityData.onlineDevices += 1;
        } else {
          cityData.offlineDevices += 1;
        }
      }
    });
  }

  // 转换为数组
  return Array.from(cityDataMap.values());
};

/**
 * 获取广东省城市列表
 * @returns {Array} - 广东省城市列表
 */
export const getGuangdongCities = () => {
  return [
    { name: "广州市" },
    { name: "韶关市" },
    { name: "深圳市" },
    { name: "珠海市" },
    { name: "汕头市" },
    { name: "佛山市" },
    { name: "江门市" },
    { name: "湛江市" },
    { name: "茂名市" },
    { name: "肇庆市" },
    { name: "惠州市" },
    { name: "梅州市" },
    { name: "汕尾市" },
    { name: "河源市" },
    { name: "阳江市" },
    { name: "清远市" },
    { name: "东莞市" },
    { name: "中山市" },
    { name: "潮州市" },
    { name: "揭阳市" },
    { name: "云浮市" },
  ];
};
